// Customer switcher in the top bar. Sourced from GET /my-customers.
//
// Routes PER ROW by the capacity held on that customer, exactly as the queue
// cards do: a `customer` grant goes to /portal, everything else to /c. It used
// to send every selection to /c unconditionally, which for a portal customer is
// a guaranteed 403 — their only navigation control led straight off their own
// surface. Routing on the row (not on "are all my grants customer grants")
// matters because one person can hold both capacities, which is the whole point
// of the multi-role design.

import { useNavigate } from 'react-router-dom';
import { useMyCustomers } from '../hooks/queries';
import { verticalLabel } from '../reviewVocab';
import { Select } from '../ui/primitives';
import { isPracticeRole } from '../reviewVocab';

export function CustomerSwitcher({ currentId }: { currentId?: string }) {
  const { data, isLoading } = useMyCustomers();
  const navigate = useNavigate();

  if (isLoading) return <span className="small faint">Loading customers…</span>;
  const customers = data ?? [];
  if (customers.length === 0)
    return <span className="small faint">No customers assigned</span>;

  const current = customers.find((c) => c.customer_id === currentId);

  const optionLabel = (c: (typeof customers)[number]) => c.customer_id === currentId
    ? c.name
    :
    `${c.name} — ${verticalLabel(c.vertical)}${
      isPracticeRole(c.my_role) ? ' (your practice)' : ''
    }`;
  const groupedOptionLabel = (c: (typeof customers)[number]) => c.customer_id === currentId
    ? c.name
    :
    `${verticalLabel(c.vertical)}${isPracticeRole(c.my_role) ? ' (your practice)' : ''}`;

  // Group parts of the SAME practice group under one optgroup, labelled by
  // the practice's name with every linked part's vertical named (W4, UX-
  // REDESIGN-BRIEF.md Addendum point 5: "the switcher shows the practice
  // once with its parts"). Additive: a customer with no practice_group (the
  // overwhelming majority today) renders exactly as before, in its own
  // ungrouped option.
  const grouped = new Map<string, typeof customers>();
  const ungrouped: typeof customers = [];
  for (const c of customers) {
    const gid = c.practice_group?.group_id;
    if (!gid) {
      ungrouped.push(c);
      continue;
    }
    const list = grouped.get(gid) ?? [];
    list.push(c);
    grouped.set(gid, list);
  }

  return (
    <div className="cx-switcher">
      <Select
        aria-label="Customer"
        // Option values are composite now, so the selected value has to be too
        // or the control shows blank while sitting on a customer.
        value={current ? `${currentId}|${current.my_role}` : ''}
        onChange={(e) => {
          // BL588. Route on a value this control actually offered, never on
          // whatever arrived: an unrecognised value used to be split into an
          // empty id and navigated to as `/c/`, which matches no route and
          // lands on the catch-all redirect home. Unreachable from the
          // rendered options, so this is robustness rather than a live defect
          // -- and the cost of not having it is a silent trip to the wrong
          // page, which is the kind of thing nobody reports.
          const picked = customers.find(
            (c) => `${c.customer_id}|${c.my_role}` === e.target.value,
          );
          if (!picked) return;
          navigate(
            isPracticeRole(picked.my_role)
              ? `/portal/${picked.customer_id}`
              : `/c/${picked.customer_id}`,
          );
        }}
      >
        {!currentId && <option value="">Select a customer…</option>}
        {ungrouped.map((c) => (
          <option
            key={`${c.customer_id}:${c.my_role}`}
            value={`${c.customer_id}|${c.my_role}`}
          >
            {optionLabel(c)}
          </option>
        ))}
        {[...grouped.entries()].map(([gid, parts]) => (
          <optgroup key={gid} label={parts[0].name}>
            {parts.map((c) => (
              <option
                key={`${c.customer_id}:${c.my_role}`}
                value={`${c.customer_id}|${c.my_role}`}
              >
                {groupedOptionLabel(c)}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
      <span className="cx-switcher__shown" aria-hidden="true" title={current?.name ?? 'Select a customer…'}>
        {current?.name ?? 'Select a customer…'}
      </span>
    </div>
  );
}
